import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BtnGhost, BtnPrimary, Field, Select } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDateTime, RETURN_STATUS } from '@/lib/labels'

type ReturnRow = {
  id: string
  status: string
  reason: string | null
  createdAt: string
  order: { reference: string }
  customer: { id: string; email: string; firstName: string } | null
  items: Array<{ id: string; quantity: number; product: { sku: string; name: string } }>
}

const STATUSES = Object.keys(RETURN_STATUS)

export function Retours() {
  const toast = useToast()
  const qc = useQueryClient()
  const [edit, setEdit] = useState<ReturnRow | null>(null)
  const [status, setStatus] = useState('OPEN')

  const list = useQuery({
    queryKey: ['admin-returns'],
    queryFn: () => api<{ items: ReturnRow[] }>('/admin/returns?limit=100'),
  })

  const patch = useMutation({
    mutationFn: () => apiMutate(`/admin/returns/${edit!.id}`, 'PATCH', { status }),
    onSuccess: () => {
      toast.ok('Retour mis à jour')
      setEdit(null)
      void qc.invalidateQueries({ queryKey: ['admin-returns'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Aucun retour" body="Les demandes de retour apparaîtront ici." />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Commande', 'Client', 'Articles', 'Statut', 'Date', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const st = RETURN_STATUS[r.status] ?? { label: r.status, tone: 'neutral' }
                return (
                  <tr key={r.id} className="border-b border-bordure-admin last:border-0">
                    <td className="px-4 py-2.5">
                      <a
                        href={`/commandes/${r.order.reference}`}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {r.order.reference}
                      </a>
                    </td>
                    <td className="px-4 py-2.5">
                      {r.customer
                        ? `${r.customer.firstName} · ${r.customer.email}`
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-second-admin">
                      {r.items
                        .map((i) => `${i.product.sku} ×${i.quantity}`)
                        .join(', ') || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill label={st.label} tone={st.tone} />
                    </td>
                    <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(r.createdAt)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <RowActions
                        items={[
                          {
                            label: 'Statut',
                            onSelect: () => {
                              setEdit(r)
                              setStatus(r.status)
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={Boolean(edit)}
        title="Changer le statut du retour"
        onClose={() => setEdit(null)}
        footer={
          <>
            <BtnGhost onClick={() => setEdit(null)}>Annuler</BtnGhost>
            <BtnPrimary disabled={patch.isPending} onClick={() => patch.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <Field label="Nouveau statut">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {RETURN_STATUS[s]?.label ?? s}
              </option>
            ))}
          </Select>
        </Field>
      </Modal>
    </div>
  )
}
